import React, { ReactNode, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { OutletRoute, PrivateRoute } from 'constants/routes';
import SideBar from 'components/SideBar';
import Stack from '@carrier-io/air-react/Stack';
import Stepper from '@carrier-io/air-react/Stepper';
import Step from '@carrier-io/air-react/Step';
import StepButton from '@carrier-io/air-react/StepButton';
import Container from '@carrier-io/air-react/Container';
import AppTopBar from 'components/AppTopBar';
import { Vendor } from 'store/models/models';
import { DataProducerProject, ProjectOnboardingStatus } from 'store/models/Project';
import { GET_VENDORS_BY_REPRESENTATIVE_ID } from 'store/graphql/queries/GET_VENDORS_BY_REPRESENTATIVE_ID';
import styles from '../../styles/Connection.module.css';
interface ConnectionProps {
  children?: ReactNode;
}

interface IStep {
  value: string;
  completed: boolean;
  active: boolean;
  disabled: boolean;
  path: string;
}

export interface ConnectionContextType {
  vendorData: Vendor;
  refetchVendorData: () => void;
  handleClick: (index: number) => void;
  handleChange: (prevIndex: number, nextIndex: number) => void;
  cancelConfigure: () => void;
}

const initSteps: IStep[] = [
  {
    value: 'Add Your Certificate',
    completed: false,
    active: true,
    disabled: false,
    path: OutletRoute.AddCertificate,
  },
  {
    value: 'Set Up Test Script',
    completed: false,
    active: false,
    disabled: true,
    path: OutletRoute.SetUpScript,
  },
  {
    value: 'Test Your Connection',
    completed: false,
    active: false,
    disabled: true,
    path: OutletRoute.TestConnection,
  },
  {
    value: 'Test a Payload',
    completed: false,
    active: false,
    disabled: true,
    path: OutletRoute.TestPayload,
  },
];

const Connection: React.FC<ConnectionProps> = () => {
  const [steps, setSteps] = useState<IStep[]>(initSteps);
  const navigate = useNavigate();
  const { representativeId = '', projectId = '' } = useParams();
  const formattedProjectId = projectId?.toLocaleLowerCase();
  const location = useLocation();
  const { data: dataUser, refetch: refetchUser } = useQuery(GET_VENDORS_BY_REPRESENTATIVE_ID, {
    variables: { representativeId: representativeId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !representativeId,
  });
  const currentProject = dataUser?.getVendorsByRepresentativeId?.[0]?.projects?.find(
    (project: DataProducerProject) => project.id === formattedProjectId,
  );
  const projectStatus: ProjectOnboardingStatus = currentProject?.status ?? ProjectOnboardingStatus.DRAFT;

  const handleClick = (index: number) => {
    setSteps((prevSteps) => {
      return prevSteps.map((step, stepIndex) => {
        const { disabled } = step;
        return index === stepIndex && !disabled ? { ...step, active: true } : { ...step };
      });
    });

    if (!steps[index].disabled) {
      navigate(steps[index].path, { replace: true });
    }
  };

  const handleChange = (prevIndex: number, nextIndex: number) => {
    setSteps((prevSteps) => {
      return prevSteps.map((step, stepIndex) => {
        if (prevIndex === stepIndex || nextIndex === prevIndex) {
          const isCompleted = nextIndex > prevIndex || nextIndex === prevIndex;

          return { ...step, completed: isCompleted, active: false };
        } else if (nextIndex === stepIndex) {
          return { ...step, active: true, disabled: false };
        }
        return { ...step };
      });
    });

    navigate(steps[nextIndex].path, { replace: true });
  };

  const cancelConfigure = () => navigate(`${PrivateRoute.VendorDashboard}/${representativeId}`, { replace: true });

  const contextValue: ConnectionContextType = {
    handleClick,
    handleChange,
    cancelConfigure,
    refetchVendorData: refetchUser,
    vendorData: dataUser?.getVendorsByRepresentativeId?.[0],
  };

  useEffect(() => {
    setSteps((prevSteps) => {
      return prevSteps.map((step) => {
        const isActive = location.pathname.includes(step.path);
        const updatedStep = { ...step, active: isActive };

        const enableSteps: OutletRoute[] = [
          OutletRoute.SetUpScript,
          OutletRoute.TestConnection,
          OutletRoute.TestPayload,
        ];
        const completeSteps: OutletRoute[] = [OutletRoute.AddCertificate, ...enableSteps];

        switch (projectStatus) {
          case ProjectOnboardingStatus.CERT_ADDED:
            if (step.path === OutletRoute.AddCertificate) {
              return { ...updatedStep, completed: true };
            }
            if (enableSteps.includes(step.path as OutletRoute)) {
              return { ...updatedStep, disabled: false };
            }
            break;

          case ProjectOnboardingStatus.CONNECTED:
            if (completeSteps.includes(step.path as OutletRoute)) {
              return { ...updatedStep, disabled: false, completed: true };
            }
            break;

          case ProjectOnboardingStatus.TESTED:
            return { ...updatedStep, disabled: false, completed: true };
        }

        return updatedStep;
      });
    });
  }, [dataUser]);

  return (
    <div className={styles.container}>
      <Stack direction="column">
        <AppTopBar />
        <Stack direction="row">
          <SideBar />
          <Stack direction="column" justifyContent="flex-start" spacing={2} className={styles.mainContent}>
            <Stack direction="column" justifyContent="flex-start" spacing={2} width="80%">
              <Container maxWidth={false}>
                <Stepper nonLinear orientation="horizontal">
                  {steps.map((step, index) => {
                    const { value, completed, active, disabled } = step;

                    return (
                      <Step data-testid="step" key={value} completed={completed} active={active} disabled={disabled}>
                        <StepButton data-testid="step-button" value={value} onClick={() => handleClick(index)}>
                          {value}
                        </StepButton>
                      </Step>
                    );
                  })}
                </Stepper>
              </Container>
            </Stack>
            <Outlet context={contextValue} />
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};

export default Connection;
